import { isDevAuthToken } from '@/lib/dev-auth';

const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? '/api/v1';

export const JWT_STORAGE_KEY = 'xat_jwt_token';
export const JWT_REFRESH_KEY = 'xat_jwt_refresh';
/** Preferência «Lembrar de mim»: `'1'` = localStorage, `'0'` = sessionStorage. */
export const JWT_REMEMBER_KEY = 'xat_jwt_remember';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly payload?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

function getRememberPreference(): boolean {
  if (typeof window === 'undefined') return true;
  const raw = window.localStorage.getItem(JWT_REMEMBER_KEY);
  // Default true para sessões antigas (token já em localStorage).
  if (raw === null) return true;
  return raw !== '0';
}

function authStorage(remember: boolean): Storage {
  return remember ? window.localStorage : window.sessionStorage;
}

export function getToken(): string | null {
  if (typeof window === 'undefined') return null;
  return (
    window.localStorage.getItem(JWT_STORAGE_KEY) ??
    window.sessionStorage.getItem(JWT_STORAGE_KEY)
  );
}

/** Segundos restantes até o JWT expirar (null se inválido). */
export function getTokenExpiresInSeconds(token?: string | null): number | null {
  const raw = token ?? getToken();
  if (!raw || isDevAuthToken(raw)) return null;
  try {
    const part = raw.split('.')[1];
    if (!part) return null;
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    const payload = JSON.parse(json) as { exp?: unknown };
    if (typeof payload.exp !== 'number') return null;
    return Math.max(0, payload.exp - Math.floor(Date.now() / 1000));
  } catch {
    return null;
  }
}

/** Agenda renovação com base no exp do token atual (útil após reload). */
export function ensureTokenRefreshScheduled(): void {
  const expiresIn = getTokenExpiresInSeconds();
  if (expiresIn != null && expiresIn > 0) {
    scheduleTokenRefresh(expiresIn);
  }
}

export function setTokens(
  accessToken: string,
  options?: { remember?: boolean; refreshToken?: string; expiresIn?: number }
): void {
  if (typeof window === 'undefined') return;

  const remember = options?.remember ?? getRememberPreference();
  window.localStorage.setItem(JWT_REMEMBER_KEY, remember ? '1' : '0');

  // Evita token duplicado nos dois storages.
  window.localStorage.removeItem(JWT_STORAGE_KEY);
  window.sessionStorage.removeItem(JWT_STORAGE_KEY);
  window.localStorage.removeItem(JWT_REFRESH_KEY);
  window.sessionStorage.removeItem(JWT_REFRESH_KEY);

  const store = authStorage(remember);
  store.setItem(JWT_STORAGE_KEY, accessToken);
  if (options?.refreshToken) {
    store.setItem(JWT_REFRESH_KEY, options.refreshToken);
  }

  window.dispatchEvent(new Event('xat-auth-changed'));

  if (typeof options?.expiresIn === 'number' && options.expiresIn > 0) {
    scheduleTokenRefresh(options.expiresIn);
  }
}

export function clearTokens(): void {
  if (typeof window === 'undefined') return;
  cancelScheduledTokenRefresh();
  window.localStorage.removeItem(JWT_STORAGE_KEY);
  window.sessionStorage.removeItem(JWT_STORAGE_KEY);
  window.localStorage.removeItem(JWT_REFRESH_KEY);
  window.sessionStorage.removeItem(JWT_REFRESH_KEY);
  window.dispatchEvent(new Event('xat-auth-changed'));
}

/** Token JWT válido para chamadas admin (exclui sessão dev local). */
export function hasApiAuthToken(): boolean {
  const token = getToken();
  return Boolean(token && !isDevAuthToken(token));
}

export function getApiBaseUrl(): string {
  return API_BASE.replace(/\/$/, '');
}

/** Converte URL de media do Laravel (relativa ou absoluta) para URL carregável no browser. */
export function resolveMediaUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  const origin = getApiBaseUrl().replace(/\/api\/v1\/?$/i, '');
  return `${origin}${url.startsWith('/') ? url : `/${url}`}`;
}

type RequestOptions = Omit<RequestInit, 'body'> & {
  body?: unknown;
  auth?: boolean;
  /** Evita novo refresh quando o retry após refresh ainda falha com 401. */
  _authRetried?: boolean;
};

const AUTH_NO_REFRESH_PATHS = new Set(['/auth/refresh', '/auth/login']);

function shouldAttemptTokenRefresh(path: string, retried: boolean): boolean {
  if (retried) return false;
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return !AUTH_NO_REFRESH_PATHS.has(normalized);
}

let refreshInFlight: Promise<boolean> | null = null;
let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let visibilityBound = false;

/** Só renova proativamente quando o JWT está perto de expirar (evita logout ao mudar de aba). */
const REFRESH_WHEN_EXPIRES_WITHIN_SEC = 5 * 60;
const REFRESH_LOCK_KEY = 'xat_jwt_refresh_lock';
const REFRESH_LOCK_TTL_MS = 12_000;

function cancelScheduledTokenRefresh(): void {
  if (refreshTimer != null) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}

function tokenNeedsRefresh(token?: string | null): boolean {
  const expiresIn = getTokenExpiresInSeconds(token);
  // Sem exp legível: não forçar refresh agressivo (deixar 401 em pedidos tratar).
  if (expiresIn == null) return false;
  return expiresIn <= REFRESH_WHEN_EXPIRES_WITHIN_SEC;
}

function tryAcquireRefreshLock(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    const now = Date.now();
    const raw = window.localStorage.getItem(REFRESH_LOCK_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as { at?: number };
      if (typeof parsed.at === 'number' && now - parsed.at < REFRESH_LOCK_TTL_MS) {
        return false;
      }
    }
    const owner = `${now}-${Math.random().toString(36).slice(2, 9)}`;
    window.localStorage.setItem(REFRESH_LOCK_KEY, JSON.stringify({ at: now, owner }));
    const check = JSON.parse(window.localStorage.getItem(REFRESH_LOCK_KEY) || '{}') as {
      owner?: string;
    };
    return check.owner === owner;
  } catch {
    return true;
  }
}

function releaseRefreshLock(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(REFRESH_LOCK_KEY);
  } catch {
    /* ignore */
  }
}

/** Renova o JWT antes de expirar enquanto o painel estiver aberto. */
export function scheduleTokenRefresh(expiresInSeconds: number): void {
  if (typeof window === 'undefined') return;
  cancelScheduledTokenRefresh();

  // Renova ~2 min antes do fim (mín. 30s).
  const delayMs = Math.max(30_000, (expiresInSeconds - 120) * 1000);
  refreshTimer = setTimeout(() => {
    void tryRefreshToken({ force: true });
  }, delayMs);

  if (!visibilityBound) {
    visibilityBound = true;
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') return;
      const token = getToken();
      if (!token || isDevAuthToken(token)) return;
      // Não renovar só por mudar de aba — isso invalida o JWT (blacklist) e provoca logout.
      if (!tokenNeedsRefresh(token)) return;
      void tryRefreshToken({ force: true });
    });

    // Outra aba renovou o token — reagenda sem limpar a sessão.
    window.addEventListener('storage', (event) => {
      if (event.key !== JWT_STORAGE_KEY || !event.newValue) return;
      const expiresIn = getTokenExpiresInSeconds(event.newValue);
      if (expiresIn != null && expiresIn > 0) {
        scheduleTokenRefresh(expiresIn);
      }
    });
  }
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, auth = false, headers: customHeaders, _authRetried = false, ...rest } = options;
  const headers = new Headers(customHeaders);

  if (!headers.has('Accept')) {
    headers.set('Accept', 'application/json');
  }

  if (body !== undefined && !(body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }

  if (auth) {
    const token = getToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);
  }

  const url = `${getApiBaseUrl()}${path.startsWith('/') ? path : `/${path}`}`;

  const response = await fetch(url, {
    ...rest,
    headers,
    body: body instanceof FormData ? body : body !== undefined ? JSON.stringify(body) : undefined,
  });

  const text = await response.text();
  let payload: unknown = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = text;
    }
  }

  if (response.status === 401 && auth && shouldAttemptTokenRefresh(path, _authRetried)) {
    if (isDevAuthToken(getToken())) {
      throw new ApiError(401, 'Sessão de desenvolvimento — API indisponível.', payload);
    }

    const refreshed = await tryRefreshToken({ force: true });
    if (refreshed) {
      return apiRequest<T>(path, { ...options, _authRetried: true });
    }
  }

  if (!response.ok) {
    let message = `Erro HTTP ${response.status}`;
    if (typeof payload === 'object' && payload !== null) {
      const body = payload as {
        message?: unknown;
        errors?: Record<string, string[] | string>;
      };
      if (body.errors && typeof body.errors === 'object') {
        const first = Object.values(body.errors)[0];
        const detail = Array.isArray(first) ? first[0] : first;
        if (detail) message = String(detail);
        else if (body.message != null) message = String(body.message);
      } else if (body.message != null) {
        message = String(body.message);
      }
    }
    throw new ApiError(response.status, message, payload);
  }

  return payload as T;
}

async function tryRefreshToken(options?: { force?: boolean }): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    const token = getToken();
    if (!token || isDevAuthToken(token)) return false;

    if (!options?.force && !tokenNeedsRefresh(token)) {
      return true;
    }

    // Outra aba já está a renovar — espera e reutiliza o token novo.
    if (!tryAcquireRefreshLock()) {
      await new Promise((r) => setTimeout(r, 800));
      const latest = getToken();
      if (latest && latest !== token && !isDevAuthToken(latest)) {
        const expiresIn = getTokenExpiresInSeconds(latest);
        if (expiresIn != null && expiresIn > 0) scheduleTokenRefresh(expiresIn);
        return true;
      }
      // Sem token novo visível: tenta mesmo assim se ainda precisar.
      if (!tokenNeedsRefresh(latest) && !options?.force) return true;
    }

    try {
      const response = await fetch(`${getApiBaseUrl()}/auth/refresh`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      const text = await response.text();
      let payload: unknown = null;
      if (text) {
        try {
          payload = JSON.parse(text);
        } catch {
          payload = text;
        }
      }

      if (!response.ok) {
        // Outra aba pode ter renovado entretanto (token antigo na blacklist).
        const current = getToken();
        if (current && current !== token && !isDevAuthToken(current)) {
          const expiresIn = getTokenExpiresInSeconds(current);
          if (expiresIn != null && expiresIn > 0) scheduleTokenRefresh(expiresIn);
          return true;
        }
        // Só desliga a sessão em 401/403 reais — não em rede/5xx.
        if (response.status === 401 || response.status === 403) {
          clearTokens();
        }
        return false;
      }

      const accessToken =
        typeof payload === 'object' &&
        payload !== null &&
        'access_token' in payload &&
        typeof (payload as { access_token: unknown }).access_token === 'string'
          ? (payload as { access_token: string }).access_token
          : '';

      if (!accessToken) {
        const current = getToken();
        if (current && current !== token && !isDevAuthToken(current)) return true;
        clearTokens();
        return false;
      }

      const expiresIn =
        typeof payload === 'object' &&
        payload !== null &&
        'expires_in' in payload &&
        typeof (payload as { expires_in: unknown }).expires_in === 'number'
          ? (payload as { expires_in: number }).expires_in
          : undefined;

      setTokens(accessToken, { expiresIn });
      return true;
    } catch {
      // Rede / CORS / timeout — mantém o token atual.
      return false;
    } finally {
      releaseRefreshLock();
    }
  })();

  try {
    return await refreshInFlight;
  } finally {
    refreshInFlight = null;
  }
}

export async function apiGet<T>(path: string, auth = false): Promise<T> {
  return apiRequest<T>(path, { method: 'GET', auth, cache: 'no-store' });
}

export async function apiPost<T>(path: string, body?: unknown, auth = false): Promise<T> {
  return apiRequest<T>(path, { method: 'POST', body, auth });
}

export async function apiPut<T>(path: string, body?: unknown, auth = true): Promise<T> {
  return apiRequest<T>(path, { method: 'PUT', body, auth });
}

export async function apiDelete<T>(path: string, auth = true): Promise<T> {
  return apiRequest<T>(path, { method: 'DELETE', auth });
}
